import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Rocket as RocketIcon } from "lucide-react";
import FavHeart from "./FavHeart";
import ShareButton from "./ShareButton";
import PollWidget from "./PollWidget";
import { api } from "../lib/api";
import { formatDateTime, relativeTime, statusColor } from "../lib/format";

export default function LaunchCard({ launch }) {
  const { id, name, status, net, pad, rocket, mission, image } = launch;
  const [poll, setPoll] = useState(null);
  const statusName = status?.name || "UNKNOWN";
  const statusAbbr = status?.abbrev || statusName;
  const color = statusColor(statusName);
  const img = image || rocket?.configuration?.image_url;
  const site = pad?.location?.name || "TBD";
  const rocketName = rocket?.configuration?.full_name || rocket?.configuration?.name || "—";

  useEffect(() => {
    let cancelled = false;
    api.get(`/polls/by-launch/${id}`)
      .then(r => { if (!cancelled) setPoll(r.data?.poll || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  return (
    <Link to={`/launches/${id}`}
      data-testid={`launch-card-${id}`}
      className="sat-surface sat-brackets group block relative transition-all hover:sat-box-glow">
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-0">
        <div className="h-36 md:h-full min-h-[160px] bg-black relative overflow-hidden">
          {img ? (
            <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><RocketIcon size={40} className="text-[color:var(--sat-border-bright)]" /></div>
          )}
          <div className="absolute top-2 left-2 border border-current px-2 py-1 font-mono text-[9px] tracking-widest" style={{ color }}>
            {statusAbbr}
          </div>
        </div>
        <div className="p-5 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="sat-label sat-cyan">{relativeTime(net)}</span>
            <div className="flex items-center gap-1.5">
              <ShareButton url={`${window.location.origin}/launches/${id}`} title={name} size={12} testid={`launch-share-${id}`} />
              <FavHeart kind="launch" ref_id={id} title={name} image={img} payload={{ net, statusName }} size={13} />
            </div>
          </div>
          <h3 className="font-display font-bold text-xl leading-tight group-hover:text-[color:var(--sat-cyan)] transition-colors">
            {name}
          </h3>
          <p className="text-sm text-[color:var(--sat-text-dim)] font-mono">{rocketName}</p>
          <div className="mt-auto pt-3 flex flex-wrap gap-x-6 gap-y-1">
            <div className="flex items-center gap-2 sat-label">
              <MapPin size={11} /> <span>{site}</span>
            </div>
            <div className="sat-label">{formatDateTime(net)}</div>
          </div>
          {poll && (
            <div className="mt-3 pt-3 border-t border-[color:var(--sat-border)]">
              <PollWidget poll={poll} launchId={id} variant="compact" />
            </div>
          )}
        </div>
        <div className="hidden md:flex items-center pr-5 text-[color:var(--sat-text-dim)] group-hover:text-[color:var(--sat-cyan)] transition-colors">
          <ChevronRight size={20} />
        </div>
      </div>
    </Link>
  );
}
